'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { toast, Toaster } from 'sonner';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

const supabase = createClient();

interface AccessoFisico {
  id: string;
  persona: string;
  badge_id: string;
  tipo: string;
  zona_id: string | null;
  timestamp: string;
  autorizzato: boolean;
  motivo: string;
  note?: string;
  zona?: { id: string; nome: string } | null;
}

interface ZonaFisica {
  id: string;
  nome: string;
  tipo: string;
}

const TIPO_BG: Record<string, string> = {
  entrata: 'bg-emerald-500/20 text-emerald-400',
  uscita: 'bg-sky-500/20 text-sky-400',
  tentativo_fallito: 'bg-red-500/20 text-red-400',
  allarme: 'bg-red-700/30 text-red-300',
};

const TIPO_LABELS: Record<string, string> = {
  entrata: 'Entrata',
  uscita: 'Uscita',
  tentativo_fallito: 'Tentativo Fallito',
  allarme: 'Allarme',
};

export default function AccessiFisiciContent() {
  const [accessi, setAccessi] = useState<AccessoFisico[]>([]);
  const [zone, setZone] = useState<ZonaFisica[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<AccessoFisico | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({ open: false, id: '', name: '' });
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterZona, setFilterZona] = useState('all');
  const [search, setSearch] = useState('');

  const emptyForm = { persona: '', badge_id: '', tipo: 'entrata', zona_id: '', motivo: '', note: '', autorizzato: true };
  const [form, setForm] = useState<typeof emptyForm>({ ...emptyForm });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [accessiRes, zoneRes] = await Promise.all([
        supabase.from('accessi_fisici').select('*, zona:zone_fisiche(id,nome,tipo)').order('timestamp', { ascending: false }).limit(200),
        supabase.from('zone_fisiche').select('id,nome,tipo').order('nome'),
      ]);
      if (accessiRes.error) throw accessiRes.error;
      if (zoneRes.error) throw zoneRes.error;
      setAccessi(accessiRes.data || []);
      setZone(zoneRes.data || []);
    } catch (err: any) {
      toast.error('Errore caricamento: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ ...emptyForm, timestamp: new Date().toISOString().slice(0, 16) } as any);
    setShowForm(true);
  };

  const openEdit = (a: AccessoFisico) => {
    setEditItem(a);
    setForm({
      persona: a.persona,
      badge_id: a.badge_id || '',
      tipo: a.tipo,
      zona_id: a.zona_id || '',
      motivo: a.motivo || '',
      note: a.note || '',
      autorizzato: a.autorizzato,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.persona.trim()) { toast.error('Nome persona obbligatorio'); return; }
    setSaving(true);
    try {
      const payload = {
        persona: form.persona.trim(),
        badge_id: form.badge_id || null,
        tipo: form.tipo,
        zona_id: form.zona_id || null,
        motivo: form.motivo || null,
        note: form.note || null,
        autorizzato: form.autorizzato,
        timestamp: editItem ? editItem.timestamp : new Date().toISOString(),
      };
      if (editItem) {
        const { error } = await supabase.from('accessi_fisici').update(payload).eq('id', editItem.id);
        if (error) throw error;
        toast.success('Accesso aggiornato');
      } else {
        const { error } = await supabase.from('accessi_fisici').insert(payload);
        if (error) throw error;
        toast.success('Accesso registrato');
      }
      setShowForm(false);
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase.from('accessi_fisici').delete().eq('id', deleteConfirm.id);
      if (error) throw error;
      toast.success('Accesso eliminato');
      setDeleteConfirm({ open: false, id: '', name: '' });
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const filtered = accessi.filter(a => {
    const matchTipo = filterTipo === 'all' || a.tipo === filterTipo;
    const matchZona = filterZona === 'all' || a.zona_id === filterZona;
    const matchSearch = !search || a.persona.toLowerCase().includes(search.toLowerCase()) || (a.badge_id || '').toLowerCase().includes(search.toLowerCase());
    return matchTipo && matchZona && matchSearch;
  });

  const totaleOggi = accessi.filter(a => new Date(a.timestamp).toDateString() === new Date().toDateString()).length;
  const tentativiFalliti = accessi.filter(a => a.tipo === 'tentativo_fallito').length;
  const nonAutorizzati = accessi.filter(a => !a.autorizzato).length;

  if (loading) return (
    <AppLayout>
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <Toaster position="bottom-right" theme="dark" richColors />
      <div className="p-6 space-y-6">

        <ConfirmDialog
          open={deleteConfirm.open}
          title="Elimina Accesso"
          message={`Sei sicuro di voler eliminare l'accesso di "${deleteConfirm.name}"?`}
          confirmLabel="Elimina"
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirm({ open: false, id: '', name: '' })}
          danger
        />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Accessi Fisici</h1>
            <p className="text-muted-color text-sm mt-1">Registro accessi fisici alle zone protette</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 text-sky-400 rounded-lg hover:bg-sky-500/20 transition-colors text-sm font-semibold">
              <Icon name="ArrowPathIcon" size={16} />
              Aggiorna
            </button>
            <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold">
              <Icon name="PlusIcon" size={16} />
              Registra Accesso
            </button>
          </div>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Accessi Totali</p>
            <p className="text-2xl font-bold text-white">{accessi.length}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Oggi</p>
            <p className="text-2xl font-bold text-sky-400">{totaleOggi}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Tentativi Falliti</p>
            <p className={`text-2xl font-bold ${tentativiFalliti > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{tentativiFalliti}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Non Autorizzati</p>
            <p className={`text-2xl font-bold ${nonAutorizzati > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>{nonAutorizzati}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-color" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Cerca persona o badge..."
              className="w-full bg-surface border border-border rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500"
            />
          </div>
          <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)} className="bg-surface border border-border rounded-lg px-3 py-2.5 text-sm text-secondary-color focus:outline-none focus:border-sky-500">
            <option value="all">Tutti i tipi</option>
            <option value="entrata">Entrata</option>
            <option value="uscita">Uscita</option>
            <option value="tentativo_fallito">Tentativo Fallito</option>
            <option value="allarme">Allarme</option>
          </select>
          <select value={filterZona} onChange={e => setFilterZona(e.target.value)} className="bg-surface border border-border rounded-lg px-3 py-2.5 text-sm text-secondary-color focus:outline-none focus:border-sky-500">
            <option value="all">Tutte le zone</option>
            {zone.map(z => <option key={z.id} value={z.id}>{z.nome}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-elevated/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Persona</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Badge</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Zona</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Autorizzato</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Motivo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-color uppercase tracking-wide">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-muted-color">
                    <Icon name="KeyIcon" size={36} className="mx-auto mb-3 opacity-30" />
                    <p>Nessun accesso trovato</p>
                  </td>
                </tr>
              ) : filtered.map((a, idx) => (
                <tr key={a.id} className={`border-b border-border/50 hover:bg-surface-elevated/40 transition-colors ${idx % 2 !== 0 ? 'bg-surface-elevated/20' : ''}`}>
                  <td className="px-4 py-3 font-semibold text-white">{a.persona}</td>
                  <td className="px-4 py-3 font-mono text-secondary-color text-xs">{a.badge_id || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${TIPO_BG[a.tipo] || 'bg-gray-500/20 text-gray-400'}`}>
                      {TIPO_LABELS[a.tipo] || a.tipo}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-secondary-color">{a.zona?.nome || '—'}</td>
                  <td className="px-4 py-3 text-muted-color text-xs">{new Date(a.timestamp).toLocaleString('it-IT')}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${a.autorizzato ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                      {a.autorizzato ? 'Sì' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-secondary-color text-xs max-w-32 truncate">{a.motivo || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(a)} className="p-1.5 rounded-lg hover:bg-sky-500/10 text-muted-color hover:text-sky-400 transition-colors">
                        <Icon name="PencilIcon" size={14} />
                      </button>
                      <button onClick={() => setDeleteConfirm({ open: true, id: a.id, name: a.persona })} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors">
                        <Icon name="TrashIcon" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-lg shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-white">{editItem ? 'Modifica Accesso' : 'Registra Accesso'}</h3>
                <button onClick={() => setShowForm(false)} className="text-muted-color hover:text-white"><Icon name="XMarkIcon" size={20} /></button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Persona *</label>
                    <input value={form.persona} onChange={e => setForm(p => ({ ...p, persona: e.target.value }))} required
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Badge ID</label>
                    <input value={form.badge_id} onChange={e => setForm(p => ({ ...p, badge_id: e.target.value }))}
                      placeholder="es. BADGE-001"
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Tipo</label>
                    <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                      <option value="entrata">Entrata</option>
                      <option value="uscita">Uscita</option>
                      <option value="tentativo_fallito">Tentativo Fallito</option>
                      <option value="allarme">Allarme</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Zona</label>
                    <select value={form.zona_id} onChange={e => setForm(p => ({ ...p, zona_id: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                      <option value="">— Zona —</option>
                      {zone.map(z => <option key={z.id} value={z.id}>{z.nome}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Motivo</label>
                  <input value={form.motivo} onChange={e => setForm(p => ({ ...p, motivo: e.target.value }))}
                    placeholder="Motivo dell'accesso"
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-xs font-semibold text-muted-color">Autorizzato</label>
                  <button
                    type="button"
                    onClick={() => setForm(p => ({ ...p, autorizzato: !p.autorizzato }))}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.autorizzato ? 'bg-emerald-500' : 'bg-gray-600'}`}
                  >
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${form.autorizzato ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                  <span className={`text-xs font-semibold ${form.autorizzato ? 'text-emerald-400' : 'text-red-400'}`}>{form.autorizzato ? 'Sì' : 'No'}</span>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Note</label>
                  <textarea value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} rows={2}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500 resize-none" />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
                <button type="button" onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold disabled:opacity-50">
                  {saving ? 'Salvataggio...' : editItem ? 'Aggiorna' : 'Registra'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
